import { useRef, useEffect, useMemo } from 'react';

import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { useMap } from '@vis.gl/react-google-maps';

import { DeckGLRef, DeckProps, MapViewState, ViewStateChangeParameters } from '../../types';

interface DeckGLOverlayProps {
    deckRef: (r: DeckGLRef | null) => void;
    effects?: DeckProps['effects'];
    gestureHandling?: google.maps.MapOptions['gestureHandling'];
    getTooltip?: DeckProps['getTooltip'];
    layers: DeckProps['layers'];
    onClick?: DeckProps['onClick'];
    onHover?: DeckProps['onHover'];
    onViewStateChange?: (args: { viewState: ViewStateChangeParameters['viewState'] }) => void;
    onInteractionStateChange?: (args: { isZooming?: boolean; isDragging?: boolean }) => void;
    pickingRadius?: DeckProps['pickingRadius'];
    viewState: MapViewState;
}

/**
 * This component instantiates a Google Maps overlay, and adds it to the map instance that contains it.
 * It synchronizes Deck.GL layers and effects with the Google Maps instance, manages user interactions,
 * and keeps the map view state in sync with React and Redux.
 */
export const DeckGLOverlay = ({
    deckRef,
    effects,
    gestureHandling,
    getTooltip,
    layers,
    onClick,
    onHover,
    onViewStateChange,
    onInteractionStateChange,
    pickingRadius,
    viewState
}: DeckGLOverlayProps) => {
    const map = useMap();
    const isUserInteractingRef = useRef(false);

    /**
     * Create a map overlay
     * https://deck.gl/docs/api-reference/google-maps/google-maps-overlay
     */
    const overlay = useMemo(() => {
        return new Proxy(new GoogleMapsOverlay({ interleaved: false }), {
            set(target, prop, value, receiver) {
                if (prop === '_deck' && value) {
                    deckRef?.(value);
                }
                return Reflect.set(target, prop, value, receiver);
            }
        });
    }, []);

    /** Keep DeckGL overlay props in sync with React props. */
    useEffect(() => {
        overlay.setProps({
            ...(effects && { effects }),
            getTooltip,
            layers,
            onClick,
            onHover,
            pickingRadius,
            useDevicePixels: true,
            /*
             * For getCursor, change the cursor to click if there is something
             * clickable on the map. getCursor code is based on this pr comment: https://github.com/visgl/deck.gl/issues/4548#issuecomment-1542622959
             * getCursor reference: https://developers.google.com/maps/documentation/javascript/reference/marker#Marker.getCursor
             */
            getCursor: ({ isHovering }) => {
                if (map) {
                    if (isHovering && gestureHandling !== 'none') {
                        // https://developers.google.com/maps/documentation/javascript/reference/map#MapOptions.draggableCursor
                        map.setOptions({ draggableCursor: 'pointer' });
                    } else {
                        map.setOptions({ draggableCursor: '' });
                    }
                }
                return 'grab';
            }
        });
    }, [overlay, effects, getTooltip, layers, onClick, pickingRadius, map, gestureHandling]);

    /** Add the overlay to the Google Map and clean it up on unmount. */
    useEffect(() => {
        overlay.setMap(map);

        return () => {
            overlay.finalize();
        };
    }, [map, overlay]);

    const updateViewState = (map: google.maps.Map) => {
        const center = map.getCenter();
        const zoom = map.getZoom();
        const { clientHeight: height, clientWidth: width } = map.getDiv();
        const newViewState = {
            ...(center ? { latitude: center.lat(), longitude: center.lng() } : {}),
            ...(zoom ? { zoom: zoom - 1 } : {}),
            ...(height && width ? { height, width } : {})
        };
        if (center || zoom || (height && width)) {
            onViewStateChange?.({ viewState: newViewState });
        }
    };

    // This use effect listens to google maps changes and updates the Redux store.
    useEffect(() => {
        if (map) {
            // Fired when the viewport bounds have changed
            // https://developers.google.com/maps/documentation/javascript/reference/map#Map.bounds_changed
            const boundsListener = map.addListener('bounds_changed', () => updateViewState(map));

            // Fired when the map center property changes
            // https://developers.google.com/maps/documentation/javascript/reference/map#Map.center_changed
            const centerListener = map.addListener('center_changed', () => updateViewState(map));

            // Fired when the map zoom property changes
            // https://developers.google.com/maps/documentation/javascript/reference/map#Map.zoom_changed
            const zoomListener = map.addListener('zoom_changed', () => {
                updateViewState(map);
                onInteractionStateChange?.({ isZooming: true });
                isUserInteractingRef.current = true;
            });

            // Fired when the user drags the map
            // https://developers.google.com/maps/documentation/javascript/reference/map#Map.drag
            const dragListener = map.addListener('drag', () => {
                onInteractionStateChange?.({ isDragging: true });
                isUserInteractingRef.current = true;
            });

            // Fired when the map becomes idle after panning or zooming
            // https://developers.google.com/maps/documentation/javascript/reference/map#Map.idle
            const mapIdleListener = map.addListener('idle', () => {
                onInteractionStateChange?.({ isZooming: false, isDragging: false });
                isUserInteractingRef.current = false;
            });

            return () => {
                if (google.maps) {
                    google.maps.event.removeListener(boundsListener);
                    google.maps.event.removeListener(centerListener);
                    google.maps.event.removeListener(zoomListener);
                    google.maps.event.removeListener(dragListener);
                    google.maps.event.removeListener(mapIdleListener);
                }
            };
        }
    }, [map]);

    /**
     * This use effect is used to keep the redux store and google map in sync. When
     * the user stops interacting with the map (zooming or panning), this logic gets executed.
     */
    useEffect(() => {
        if (map) {
            // Skip updates during user interactions to prevent freezing the map
            if (isUserInteractingRef.current) {
                return;
            }

            let moveCameraOptions: google.maps.CameraOptions = {};
            const { latitude, longitude, zoom } = viewState;
            const center = map.getCenter();
            const lat = center?.lat();
            const lng = center?.lng();
            const mapZoom = map.getZoom();
            // If the values passed into the component are different than Google Map's
            // internal state, we add to an object to update Google Map's internal state.
            if (latitude !== undefined && longitude !== undefined && (latitude !== lat || longitude !== lng)) {
                moveCameraOptions = { ...moveCameraOptions, center: { lat: latitude, lng: longitude } };
            }
            if (mapZoom !== undefined && zoom !== mapZoom - 1) {
                moveCameraOptions = { ...moveCameraOptions, zoom: zoom + 1 };
            }
            // We only call moveCamera if the states are different. Otherwise,
            // if a user is panning or zooming and we call moveCamera, it stops
            // the pan or zoom action and creates a clunky experience.
            if (Object.keys(moveCameraOptions).length > 0) {
                map.moveCamera(moveCameraOptions);
            }
        }
    }, [map, viewState]);

    // This component doesn't render any DOM elements. All map and overlay logic is handled through the Google Maps and Deck.GL APIs.
    return null;
};
